import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomerPageLayout from "../customer-page-layout/CustomerPageLayout";
import { CustomerLoader, SuccessScreen } from "../components";
import { useGetFriendPaymentRequestByTokenQuery } from "../../../service/charge-service";
import { formatCurrency } from "../../../utils";
import { TRANSACTION_STATUS } from "../../../model/transaction";
import { FailedTransactionIcon, CheckmarkCircleLgIcon } from "../../../assets/icons";
import { ApplicationRoutes } from "../../../routes";
import { resolveUtilityName } from "../../../tenant/branding/resolveTenant";

export const PayRequestPage = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { token } = useParams<{ token: string }>();
	const { data, isLoading, isError } = useGetFriendPaymentRequestByTokenQuery(
		{ token: token ?? "", utilityName: resolveUtilityName() },
		{ skip: !token }
	);

	useEffect(() => {
		if (!data) return;
		if (data.status === TRANSACTION_STATUS.IN_PROGRESS && data.paymentLink) {
			window.location.href = data.paymentLink;
		}
	}, [data]);

	const goToBase = () => navigate(ApplicationRoutes.LANDING_PAGE);

	if (!token || isError) {
		return (
			<CustomerPageLayout showBackButton={false}>
				<SuccessScreen
					icon={<FailedTransactionIcon />}
					title={t("payment.paymentFailed")}
					subtitle={t("history.web.failedToLoadTransactionDetails")}
					buttonText={t("common.actions.done")}
					onContinue={goToBase}
				/>
			</CustomerPageLayout>
		);
	}

	if (isLoading || !data) {
		return (
			<CustomerPageLayout showBackButton={false}>
				<CustomerLoader subtitle={t("history.web.loadingTransactionDetails")} />
			</CustomerPageLayout>
		);
	}

	if (data.status === TRANSACTION_STATUS.IN_PROGRESS) {
		return (
			<CustomerPageLayout showBackButton={false}>
				<CustomerLoader title={t("buyElectricity.web.redirectingToPayment")} subtitle={t("buyElectricity.web.redirectToProviderNote")} />
			</CustomerPageLayout>
		);
	}

	const amountText = formatCurrency(data.amount.amount, { currency: data.amount.currency });

	if (data.status === TRANSACTION_STATUS.COMPLETED) {
		return (
			<CustomerPageLayout showBackButton={false}>
				<SuccessScreen
					//icon={<CheckmarkCircleLgIcon />}
					title={t("payment.paymentSuccessfull")}
					subtitle={amountText}
					buttonText={t("common.actions.done")}
					onContinue={goToBase}
				/>
			</CustomerPageLayout>
		);
	}

	const title =
		data.status === TRANSACTION_STATUS.EXPIRED
			? t("payment.transactionExpired")
			: data.status === TRANSACTION_STATUS.CANCELLED
				? t("payment.transactionCancelled")
				: t("payment.paymentFailed");

	return (
		<CustomerPageLayout showBackButton={false}>
			<SuccessScreen icon={<FailedTransactionIcon />} title={title} subtitle={amountText} buttonText={t("common.actions.done")} onContinue={goToBase} />
		</CustomerPageLayout>
	);
};
